/**
 * Ship display names.
 *
 * `ship_id` (`A-PM`) stays the identifier everywhere it matters - requests,
 * share links, saved views, S3 partitions - so it never has to be translated
 * back. These helpers exist purely so people read `AIDAprima` instead.
 *
 * The map is fetched once at boot from `/api/ships/names` and is already keyed
 * by id, so every lookup is a plain object read. Anything missing falls back to
 * the code itself: a ship that reaches the UI without a mapping still labels
 * itself, rather than rendering blank.
 */

import { shipMeta } from '../state/store.ts';

/** What to show the user for a ship code. */
export function shipLabel(shipId: string): string {
  return shipMeta.value[shipId]?.name || shipId;
}

/** Brand a ship belongs to, or `''` when unknown. */
export function shipBrand(shipId: string): string {
  return shipMeta.value[shipId]?.brand ?? '';
}

/** Label several ships for one line of prose, e.g. a warning. */
export function shipLabels(shipIds: string[]): string {
  return shipIds.map(shipLabel).join(', ');
}

/**
 * Ships bucketed by brand, brands and ships each alphabetical.
 *
 * Unmapped ships collect under "Other" rather than being dropped - the picker
 * must always be able to show everything the data bucket has.
 */
export function groupShipsByBrand(shipIds: string[]): Array<{ brand: string; ships: string[] }> {
  const byBrand = new Map<string, string[]>();
  for (const id of shipIds) {
    const brand = shipBrand(id) || 'Other';
    const list = byBrand.get(brand);
    if (list) list.push(id);
    else byBrand.set(brand, [id]);
  }
  return [...byBrand.entries()]
    .map(([brand, ships]) => ({
      brand,
      ships: ships.sort((a, b) => shipLabel(a).localeCompare(shipLabel(b))),
    }))
    .sort((a, b) => {
      // "Other" last; everything else alphabetical.
      if (a.brand === 'Other') return 1;
      if (b.brand === 'Other') return -1;
      return a.brand.localeCompare(b.brand);
    });
}
